import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, catchError, map, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ErrorHandlingService } from '../general/error-handling.service';
import {
  IPathwayEnrollmentRequest,
  IPathwayEnrollmentResponse,
  IPathwayNextStepResponse,
  IPathwayRunStepResponse,
  IPathwayCompleteStepResponse,
} from 'src/app/interfaces/ipathway-enrollment';
import { IPathwayDetailResponse } from 'src/app/interfaces/ipathway-details-response';
import { IPdfLearnState } from 'src/app/interfaces/ipdf-learn-state';
import { IVideoLearnState } from 'src/app/interfaces/ivideo-learn-state';

@Injectable({
  providedIn: 'root',
})
export class PathwaysService {
  backendUrl: string = environment.backendUrl;

  constructor(
    private http: HttpClient,
    private errorHandlingService: ErrorHandlingService
  ) {}

  getorCreatePathwayEnrollment(
    moduleAssignmentUuid: string
  ): Observable<IPathwayEnrollmentResponse> {
    if (!moduleAssignmentUuid) {
      throw new Error('moduleAssignmentUuid is required');
    }

    const requestBody: IPathwayEnrollmentRequest = {
      moduleAssignmentUuid,
    };

    return this.http
      .post<IPathwayEnrollmentResponse>(
        `${this.backendUrl}/pathwayLearner/getEnrollment`,
        requestBody
      )
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`getorCreatePathwayEnrollment: ${outcome}`);
        }),
        catchError(
          this.errorHandlingService.handleError<IPathwayEnrollmentResponse>(
            'getorCreatePathwayEnrollment request'
          )
        )
      );
  }

  createEnrollment(
    moduleAssignmentUuid: string
  ): Observable<IPathwayEnrollmentResponse> {
    if (!moduleAssignmentUuid) {
      throw new Error('moduleAssignmentUuid is required');
    }

    const requestBody: IPathwayEnrollmentRequest = {
      moduleAssignmentUuid,
    };

    return this.http
      .post<IPathwayEnrollmentResponse>(
        `${this.backendUrl}/pathwayLearner/createEnrollment`,
        requestBody
      )
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'created' : 'did not create';
          console.log(`createEnrollment: ${outcome}`);
        }),
        catchError(
          this.errorHandlingService.handleError<IPathwayEnrollmentResponse>(
            'createEnrollment request'
          )
        )
      );
  }

  getNextStepByEnrollmentUuid(
    enrollmentUuid: string
  ): Observable<IPathwayNextStepResponse> {
    if (!enrollmentUuid) {
      throw new Error('enrollmentUuid is required');
    }

    const params = new HttpParams().set('enrollmentUuid', enrollmentUuid);

    return this.http
      .get<IPathwayNextStepResponse>(
        `${this.backendUrl}/pathwayLearner/getNextStepByEnrollmentUuid`,
        { params }
      )
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`getNextStepByEnrollmentUuid: ${outcome}`);
        }),
        catchError(
          this.errorHandlingService.handleError<IPathwayNextStepResponse>(
            'getNextStepByEnrollmentUuid request'
          )
        )
      );
  }

  runStepByEnrollmentUuid(
    enrollmentUuid: string,
    stepUuid: string
  ): Observable<IPathwayRunStepResponse> {
    if (!enrollmentUuid) {
      throw new Error('enrollmentUuid is required');
    }
    if (!stepUuid) {
      throw new Error('stepUuid is required');
    }

    const params = new HttpParams()
      .set('enrollmentUuid', enrollmentUuid)
      .set('stepUuid', stepUuid);

    return this.http
      .post<IPathwayRunStepResponse>(
        `${this.backendUrl}/pathwayLearner/runStepByEnrollmentUuid`,
        null,
        { params }
      )
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'executed' : 'did not execute';
          console.log(`runStepByEnrollmentUuid: ${outcome}`);
        }),
        catchError(
          this.errorHandlingService.handleError<IPathwayRunStepResponse>(
            'runStepByEnrollmentUuid request'
          )
        )
      );
  }

  completeStepByEnrollmentUuid(
    enrollmentUuid: string
  ): Observable<IPathwayCompleteStepResponse> {
    if (!enrollmentUuid) {
      throw new Error('enrollmentUuid is required');
    }

    const params = new HttpParams().set('enrollmentUuid', enrollmentUuid);

    return this.http
      .post<IPathwayCompleteStepResponse>(
        `${this.backendUrl}/pathwayLearner/completeStepByEnrollmentUuid`,
        null,
        { params }
      )
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'completed' : 'did not complete';
          console.log(`completeStepByEnrollmentUuid: ${outcome}`);
        }),
        catchError(
          this.errorHandlingService.handleError<IPathwayCompleteStepResponse>(
            'completeStepByEnrollmentUuid request'
          )
        )
      );
  }

  getPathwayDetails(
    moduleAssignmentUuid: string,
    includeStepInfo: boolean
  ): Observable<IPathwayDetailResponse> {
    if (!moduleAssignmentUuid) {
      throw new Error('moduleAssignmentUuid is required');
    }

    if (includeStepInfo === null || includeStepInfo === undefined) {
      throw new Error('includeStepInfo is required');
    }

    const params = new HttpParams()
      .set('moduleAssignmentUuid', moduleAssignmentUuid)
      .set('includeStepInfo', includeStepInfo.toString());

    return this.http
      .get<IPathwayDetailResponse>(
        `${this.backendUrl}/pathway/getPathwayDetails`,
        { params }
      )
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'executed' : 'did not execute';
          console.log(`getPathwayDetails: ${outcome}`);
        }),
        catchError(
          this.errorHandlingService.handleError<IPathwayDetailResponse>(
            'getPathwayDetails request'
          )
        )
      );
  }

  updateInstanceProgress(
    instanceUuid: string,
    learnState: IPdfLearnState | IVideoLearnState
  ): Observable<void> {
    if (!instanceUuid) {
      throw new Error('instanceUuid is required');
    }
    if (!learnState) {
      throw new Error('learnState is required');
    }

    const body = {
      instanceUuid: instanceUuid,
      progress: JSON.stringify(learnState),
    };
    return this.http
      .post<any>(`${this.backendUrl}/pathwayLearner/updateProgress`, body)
      .pipe(
        tap(() => {
          console.log(`updateInstanceProgress: updated`);
        }),
        catchError(
          this.errorHandlingService.handleError<void>(
            'updateInstanceProgress request'
          )
        )
      );
  }
}
